var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Absolute, Box, Button, ChoiceChip, Flex, FormField, Label, Relative, Spinner, Text, Tooltip } from 'pcln-design-system';
import { ErrorToolTip, FormInput, SelectInput, SuccessIcon, WarningIcon } from './OtpForm.styles';
import { Email as EmailIcon, Smartphone as SmartphoneIcon } from 'pcln-icons';
import { uniqCountryCodes } from './helpers/buildCountryDropdown';
import otpService from '../../utils/otpService';
import { useOtpContext } from '../../context/OtpContext';
import { ChatbotDataContext } from '../../context/ChatbotDataContext';
import { otpDeliveryMechanisms } from '../../constants';
const OtpForm = () => {
  const choiceChipOptions = [{
    label: 'Send to Phone',
    icon: SmartphoneIcon,
    deliveryType: otpDeliveryMechanisms.SMS
  }, {
    label: 'Send to Email',
    icon: EmailIcon,
    deliveryType: otpDeliveryMechanisms.EMAIL
  }];
  const errorMessage = 'I’m having some trouble accessing your trip information. ';
  const [phoneColor, setPhoneColor] = useState();
  const [emailColor, setEmailColor] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [email, setEmail] = useState();
  const [countryCode, setCountryCode] = useState('1');
  const [otpFormSubmissionInProgress, setOtpFormSubmissionInProgress] = useState(false);
  const [deliveryType, setDeliveryType] = useState(otpDeliveryMechanisms.SMS_PHONE);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showSmsInput, setShowSmsInput] = useState(true);
  const [showEmailOrSmsToggle, setShowEmailOrSmsToggle] = useState(false);
  const [choiceChipSelected, setChoiceChipSelected] = useState();
  const {
    setOtpRequestErrorMsg,
    setOtpDeliveryType,
    setVerificationApiFunction,
    setShowOtpVerificationForm,
    setOtpFormData
  } = useOtpContext();
  const {
    setAccountLessTripsAccessPayload,
    submitChatMessagesPayload,
    requestFrom
  } = useContext(ChatbotDataContext);
  const {
    cguid,
    resolvedClientIP
  } = submitChatMessagesPayload || {};
  useEffect(() => {
    setOtpDeliveryType(deliveryType);
  }, [deliveryType, setOtpDeliveryType]);
  useEffect(() => {
    if (countryCode === '1') {
      // For US: only show SMS input, never show email input
      setShowSmsInput(true);
      setShowEmailInput(false);
      setShowEmailOrSmsToggle(false);
      setDeliveryType(otpDeliveryMechanisms.SMS_PHONE);
    } else {
      // For non-US countries: show both SMS and email inputs
      setShowSmsInput(true);
      setShowEmailInput(true);
    }
  }, [countryCode]);
  const isPhoneValidated = useMemo(() => {
    if (!phoneNumber) return false;
    // Check if the phone number contains only digits or has the format XXX-XXX-XXXX
    const isValidFormat = /^[0-9]+$/.test(phoneNumber) || /^\d{3}-\d{3}-\d{4}$/.test(phoneNumber);
    if (!isValidFormat) return false;
    // Remove any dashes to get just the digits
    const digits = phoneNumber.replace(/-/g, '');
    // For US/Canada (country code 1), require exactly 10 digits
    if (countryCode === '1') {
      return digits.length === 10;
    }
    // For all other country codes, require 5 or more digits
    return digits.length >= 5;
  }, [phoneNumber, countryCode]);
  const getChoiceChipLabel = option => {
    if (option.deliveryType === otpDeliveryMechanisms.SMS || option.deliveryType === otpDeliveryMechanisms.SMS_PHONE) {
      if (phoneNumber && phoneNumber.length >= 2) {
        const lastTwoDigits = phoneNumber.slice(-2);
        return `${option.label} (+${countryCode} ........${lastTwoDigits})`;
      }
    } else {
      const [localPart, domain] = email === null || email === void 0 ? void 0 : email.split('@');
      const firstLetter = localPart === null || localPart === void 0 ? void 0 : localPart.charAt(0);
      return `${option.label} (${firstLetter}...@${domain})`;
    }
    return option.label;
  };
  const isEmailValidated = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [email]);
  const disableButton = useMemo(() => {
    if (countryCode === '1') {
      // For US: only check phone validation (SMS only)
      return !isPhoneValidated;
    } else {
      // For non-US countries: check validation based on current delivery type
      return !isPhoneValidated || !isEmailValidated;
    }
  }, [isEmailValidated, isPhoneValidated, countryCode]);
  function checkPhoneValidation() {
    if (phoneNumber) {
      setPhoneColor(isPhoneValidated ? 'success' : 'error');
    }
  }
  function checkEmailValidation() {
    if (email) {
      setEmailColor(isEmailValidated ? 'success' : 'error');
    }
  }
  function handlePhoneChange(e) {
    const value = e.currentTarget.value;
    // Only allow digits
    if (/^\d*$/.test(value)) {
      setPhoneNumber(value);
    }
  }
  function handleEmailChange(e) {
    const value = e.currentTarget.value;
    setEmail(value);
  }
  function handleContinueClick(e) {
    return __awaiter(this, void 0, void 0, function* () {
      if (countryCode === '1') {
        yield submitFormHandler(e, deliveryType);
      } else {
        setShowEmailOrSmsToggle(true);
      }
    });
  }
  function submitFormHandler(e, deliveryType) {
    return __awaiter(this, void 0, void 0, function* () {
      e.preventDefault();
      setOtpRequestErrorMsg('');
      setOtpFormData({
        phoneNumber: Number(phoneNumber),
        email,
        countryCode
      });
      setAccountLessTripsAccessPayload({
        phoneNumber: Number(phoneNumber),
        emailAddress: email,
        scope: 'TRIPS'
      });
      setOtpFormSubmissionInProgress(true);
      setOtpRequestErrorMsg('');
      try {
        const otpServiceInstance = otpService({
          deliveryType
        });
        if (otpServiceInstance.verify) {
          setVerificationApiFunction(() => otpServiceInstance.verify);
        }
        const otpChallenge = yield otpServiceInstance.createChallenge({
          phoneNumber,
          email,
          countryCode,
          scope: 'trips',
          requestHeaders: {
            'x-pcln-client-ip': resolvedClientIP,
            cguid
          },
          requestFrom
        });
        if (otpChallenge) {
          setShowOtpVerificationForm(true);
        } else {
          setOtpRequestErrorMsg(errorMessage);
        }
      } catch (_e) {
        setOtpRequestErrorMsg(errorMessage);
      } finally {
        setOtpFormSubmissionInProgress(false);
      }
    });
  }
  function handleChoiceChipClick(e, option) {
    return __awaiter(this, void 0, void 0, function* () {
      setDeliveryType(option.deliveryType);
      setChoiceChipSelected(option.label);
      yield submitFormHandler(e, option.deliveryType);
    });
  }
  return React.createElement(React.Fragment, null, React.createElement(Box, {
    maxWidth: '390px'
  }, otpFormSubmissionInProgress ? React.createElement(Spinner, null) : React.createElement(Box, null, React.createElement(Flex, {
    flexDirection: 'column',
    mb: 2
  }, showSmsInput && React.createElement(Flex, {
    alignItems: 'center'
  }, React.createElement(Box, {
    width: '30%',
    mr: 2
  }, React.createElement(Relative, null, React.createElement(FormField, null, React.createElement(Label, {
    htmlFor: 'countryCodes'
  }, 'Country Code \u207A'), React.createElement(SelectInput, {
    defaultValue: '1',
    id: 'countryCodes',
    name: 'countryCodes',
    value: countryCode,
    onChange: function (e) {
      setCountryCode(e.target.value);
    }
  }, uniqCountryCodes.map(country => {
    return React.createElement("option", {
      value: country.phoneCode,
      key: country.label
    }, country.label);
  })), React.createElement(SuccessIcon, {
    color: 'success'
  })), React.createElement(Absolute, {
    bg: 'background.lightest',
    top: 17,
    left: 14,
    width: '75px',
    style: {
      pointerEvents: 'none'
    }
  }, React.createElement(Box, null, React.createElement(Text, {
    fontSize: 1
  }, "+", countryCode))))), React.createElement(Box, {
    width: '70%'
  }, React.createElement(FormField, null, React.createElement(Label, {
    htmlFor: 'phone'
  }, 'Phone Number \u207A'), React.createElement(FormInput, {
    id: 'phone',
    name: 'phone',
    type: 'text',
    inputMode: 'numeric',
    pattern: '[0-9]*',
    placeholder: 'Phone number \u207A',
    onChange: handlePhoneChange,
    onFocus: function () {
      setPhoneColor(undefined);
    },
    onBlur: checkPhoneValidation,
    color: phoneColor,
    value: phoneNumber,
    "aria-describedby": 'phone-error'
  }), phoneColor === 'error' ? React.createElement(WarningIcon, {
    color: 'error'
  }) : null, phoneColor === 'success' ? React.createElement(SuccessIcon, {
    color: 'success'
  }) : null), phoneColor === 'error' ? React.createElement(ErrorToolTip, {
    id: 'phone-error',
    right: true,
    color: 'error'
  }, "Phone number is invalid") : null)), showEmailInput && React.createElement(Box, {
    my: 2
  }, React.createElement(FormField, null, React.createElement(Label, {
    htmlFor: 'email'
  }, 'Email Address \u207A'), React.createElement(FormInput, {
    id: 'email',
    name: 'email',
    placeholder: 'Email address \u207A',
    onChange: handleEmailChange,
    onFocus: function () {
      setEmailColor(undefined);
    },
    onBlur: checkEmailValidation,
    color: emailColor,
    value: email
  }), emailColor === 'error' ? React.createElement(WarningIcon, {
    color: 'error'
  }) : null, emailColor === 'success' ? React.createElement(SuccessIcon, {
    color: 'success'
  }) : null), emailColor === 'error' ? React.createElement(Tooltip, {
    id: 'email-error',
    right: true,
    color: 'error'
  }, "Email is invalid") : null)), !showEmailOrSmsToggle && React.createElement(Box, {
    mt: 1
  }, React.createElement(Button, {
    disabled: disableButton,
    onClick: handleContinueClick
  }, "Continue")))), showEmailOrSmsToggle && React.createElement(React.Fragment, null, React.createElement(Text, {
    mb: 3
  }, "Choose how you'd like to verify. We'll send a 6-digit code to one of your provided contacts."), React.createElement(Box, null, choiceChipOptions.map(option => React.createElement(Flex, {
    key: option.label,
    flexDirection: 'row',
    mb: 2
  }, React.createElement(ChoiceChip, {
    label: getChoiceChipLabel(option),
    Icon: option.icon,
    selected: choiceChipSelected === option.label,
    onClick: function (e) {
      return __awaiter(this, void 0, void 0, function* () {
        yield handleChoiceChipClick(e, option);
      });
    }
  }))))));
};
export default OtpForm;